import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { PatientsService } from '@/services/patients.service'
import { Patient } from '@/entities/patient'
import { Sex } from '@/entities/sex'
import {
  getDateSearchStrings,
  toIsoDate
} from "@/composables/useDateHelper"
import NotificationMessageService from '@/services/notificationmessage.service'
import {convertFrontendSexValueToFormBuilderValue} from "@/utils";

const service = new PatientsService()

const initialPatient: Patient = {
  patientId: 0,
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  sexId: 'm',
  street: '',
  postalCode: '',
  city: '',
  country: '',
  patientNumber: '',
  externalId: '',
}

export const usePatientsStore = defineStore('patients', () => {
  // --- state
  const patients = ref<Patient[]>([])
  const sexes = ref<Sex[]>([])
  const patient = ref<Patient>({ ...initialPatient })
  const searchInput = ref('')
  const searchCegatId = ref('')

  const matchesSearch = (value: string | number | null | undefined, search: string): boolean =>
    String(value ?? '').toLowerCase().includes(search);

  // --- getters
  const filteredPatients = computed(() => {
    if (searchCegatId.value) {
      return patients.value.filter((patient) =>
        matchesSearch(patient.patientNumber, searchCegatId.value)
      );
    }

    if (!searchInput.value) return patients.value
    const search = searchInput.value.toLowerCase()
    return patients.value.filter((patient: Patient) =>
      matchesSearch(patient.firstName, search) ||
      matchesSearch(patient.lastName, search) ||
      (patient.searchIndex?.dateOfBirth ?? []).some(str => matchesSearch(str, search)) ||
      matchesSearch(patient.patientNumber, search) ||
      matchesSearch(convertFrontendSexValueToFormBuilderValue(patient.sexId), search)
    );
  })

  const getSearchInput = computed((): string => searchInput.value)
  const getSearchCegatId = computed((): string =>  searchCegatId.value)
  const getPatients = computed((): Patient[] => filteredPatients.value)
  const getPatient = computed((): Patient => patient.value)
  const getSexes = computed((): Sex[] => sexes.value)

  const getSexById = (sexId: string): Sex =>
    sexes.value.find(s => s.sexId === sexId) ?? sexes.value[0]

  // --- actions
  async function fetchPatient(patientId: number, physicianId?: string) {
    if (patientId <= 0) return

    try {
      const patientResponse = await service.getPatient(patientId, physicianId)
      if (patientResponse) {
        patient.value = patientResponse
      }
    } catch {
      NotificationMessageService.showError(`Error while fetching patient - id: ${patientId}`)
    }
  }

  async function fetchPatients(forceUpdate = false) {
    try {
      const patientsResponse = await service.getPatients(forceUpdate)
      if (patientsResponse  && Array.isArray(patientsResponse)) {
        patients.value = buildSearchIndexes(patientsResponse)
      }
    } catch {
      NotificationMessageService.showError('Error while fetching patients!')
    }
  }

  async function fetchPatientsByCegatId(cegatId: string) {
    try {
      const patientsResponse = await service.getPatientsByCegatId(cegatId)
      if (patientsResponse) {
        patients.value = buildSearchIndexes(patientsResponse)
      } else {
        patients.value = []
      }

      searchCegatId.value = cegatId
    } catch {
      NotificationMessageService.showError('Error while fetching patients!')
    }
  }

  async function fetchPatientsByPhysicianId(physicianId: string) {
    try {
      const patientsResponse = await service.getPatientsByPhysicianId(physicianId)
      if (patientsResponse) {
        patients.value = buildSearchIndexes(patientsResponse)
      } else {
        patients.value = []
      }
    } catch {
      NotificationMessageService.showError('Error while fetching patients!')
    }
  }

  async function fetchPatientsSexes() {
    try {
      const sexesResponse = await service.getSexes()
      if (sexesResponse) {
        sexes.value = sexesResponse
      }
    } catch {
      NotificationMessageService.showError('Error while fetching patients sexes.')
    }
  }

  async function postPatient(newPatient: Patient, physicianId?: string) {
    try {
      const physId = physicianId ?? ''

      const payload = {
        ...newPatient,
        dateOfBirth: toIsoDate(newPatient.dateOfBirth) ?? ''
      }

      const res = await service.postPatient(payload, physId)
      if (res) {
        NotificationMessageService.showSuccess('New Patient Recorded!')
        patient.value = res

        if (physId) {
          await fetchPatientsByPhysicianId(physId)
        } else {
          await fetchPatients(true)
        }

        return res
      }
      return null
    } catch {
      NotificationMessageService.showError('Error while recording new patient.')
    }
  }

  async function putPatient(updated: Patient, physicianId?: string) {
    if (patient.value.patientId <= 0) return
    try {
      const physId = physicianId ?? ''
      const updatedPatient = await service.putPatient(updated, physId)
      if (updatedPatient) {
        NotificationMessageService.showSuccess('Patient Information Updated!')
        patient.value = updatedPatient
        if (physId) {
          await fetchPatientsByPhysicianId(physId)
        } else {
          await fetchPatients(true)
        }
      }
    } catch {
      NotificationMessageService.showError(`Error while updating patient - id: ${updated.patientId}.`)
    }
  }

  async function deletePatient(patientId: number, physicianId?: string) {
    if (patientId <= 0) return
    try {
      const physId = physicianId ?? ''
      const error = await service.deletePatient(patientId, physId)
      if (!error) {
        NotificationMessageService.showSuccess('Patient Deleted!')
        if (physId) {
          await fetchPatientsByPhysicianId(physId)
        } else {
          await fetchPatients(true)
        }
      }
    } catch {
      NotificationMessageService.showError(`Error while deleting patient - id: ${patientId}.`)
    }
  }

  function resetPatient() {
    patient.value = { ...initialPatient }
  }

  function resetPatients() {
    patients.value = []
  }

  function buildSearchIndexes(patientList: Patient[]): Patient[] {
    return patientList.map(p => ({
      ...p,
      searchIndex: {
        dateOfBirth: p.dateOfBirth ? getDateSearchStrings(p.dateOfBirth) : [],
      }
    }))
  }

  return {
    // state
    patients,
    sexes,
    patient,
    searchInput,
    searchCegatId,

    // getters
    filteredPatients,
    getSexById,
    getSexes,
    getPatient,
    getPatients,
    getSearchCegatId,
    getSearchInput,

    // actions
    fetchPatient,
    fetchPatients,
    fetchPatientsByCegatId,
    fetchPatientsByPhysicianId,
    fetchPatientsSexes,
    postPatient,
    putPatient,
    deletePatient,
    resetPatient,
    resetPatients,
  }
})
